import { useCallback, useEffect, useRef, useState } from "react";
import {
  acquireBlockLease, heartbeatBlockLease, releaseBlockLease, saveBlockContent,
} from "@/lib/workDocumentQueries";

const AUTOSAVE_DEBOUNCE_MS = 800;
const HEARTBEAT_INTERVAL_MS = 20000; // renews well inside the 45s server TTL

// "Save as version" must never snapshot a block whose latest keystroke is
// still sitting in its 800ms debounce window (or mid-flight to the
// server) — that would silently save an EMPTY/stale answer. Every
// useBlockLease instance registers an imperative "flush me now" function
// here; WorkDocumentVersionPanel awaits every registered block's flush
// before calling POST /versions, so the version's snapshot always
// reflects exactly what is on screen at the moment "Save as version" is
// clicked, even for a block whose debounce timer hasn't fired yet.
const _pendingFlushRegistry = new Map(); // docId -> Set<() => Promise<void>>

export function registerBlockFlush(docId, flushFn) {
  if (!_pendingFlushRegistry.has(docId)) _pendingFlushRegistry.set(docId, new Set());
  _pendingFlushRegistry.get(docId).add(flushFn);
  return () => _pendingFlushRegistry.get(docId)?.delete(flushFn);
}

export async function flushAllPendingBlockSaves(docId) {
  const fns = _pendingFlushRegistry.get(docId);
  if (!fns || fns.size === 0) return;
  await Promise.all([...fns].map((fn) => fn().catch(() => {})));
}

function draftKey(docId, blockId) {
  return `wdoc_draft:${docId}:${blockId}`;
}

function readDraft(docId, blockId) {
  try {
    const raw = localStorage.getItem(draftKey(docId, blockId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // The persisted key stays `text` for backward compatibility with
    // drafts already sitting in a user's browser from before Phase 2 —
    // it now simply carries any JSON-serializable value (string for
    // short/long/rich, array/object for list/checklist/table), never
    // just plain text.
    return { value: parsed.text, baseRevision: parsed.baseRevision, savedAt: parsed.savedAt };
  } catch {
    return null;
  }
}

function writeDraft(docId, blockId, value, baseRevision) {
  try {
    localStorage.setItem(draftKey(docId, blockId), JSON.stringify({ text: value, baseRevision, savedAt: new Date().toISOString() }));
  } catch {
    // localStorage may be unavailable (private mode, quota) — draft recovery
    // is a convenience, never a correctness requirement, so this is safe to skip.
  }
}

function clearDraft(docId, blockId) {
  try {
    localStorage.removeItem(draftKey(docId, blockId));
  } catch {
    /* see writeDraft */
  }
}

/**
 * Master v5 Prompt 8A — one lease/autosave lifecycle per block. Extended
 * in Prompt 30C Phase 2 to be value-shape-agnostic via `valueKey`
 * (default `"text"`, unchanged for every pre-existing caller): a typed
 * imported block (list/checklist/table) autosaves a structured
 * array/object under its OWN content key exactly the same way a plain
 * textarea autosaves a string under `content.text` — same lease, same
 * debounce, same conflict/recovery machinery, no per-type autosave hook.
 *
 * `serverBlock` is this block's latest view from the polling/SSE-refreshed
 * document query. Remote saved updates NEVER overwrite `localValue` while
 * the caller is actively editing (`isMine` with unsaved changes) — the
 * component only ever reads `serverBlock.content` before editing starts or
 * after a clean save, never mid-edit.
 */
export function useBlockLease({ workspaceId, docId, serverBlock, isViewAsClient, valueKey = "text" }) {
  const blockId = serverBlock.block_id;
  const [lease, setLease] = useState(null); // { lease_id, lease_token, expires_at }
  const [localValue, setLocalValue] = useState(serverBlock.content?.[valueKey] ?? null);
  const [dirty, setDirty] = useState(false);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error | conflict | locked
  const [conflictDoc, setConflictDoc] = useState(null);
  const [recoverableDraft, setRecoverableDraft] = useState(() => {
    const d = readDraft(docId, blockId);
    if (d && JSON.stringify(d.value) !== JSON.stringify(serverBlock.content?.[valueKey] ?? null)) return d;
    return null;
  });

  const leaseRef = useRef(null);
  const heartbeatTimer = useRef(null);
  const saveTimer = useRef(null);
  const idempotencyKeyRef = useRef(null);
  const baseRevisionRef = useRef(serverBlock.revision);

  useEffect(() => {
    leaseRef.current = lease;
  }, [lease]);

  // A remote save by someone else (block moved to editing_by_other / saved)
  // only ever updates our *base revision* reference — never the live
  // textarea buffer while we hold an unsaved edit.
  useEffect(() => {
    if (!dirty) {
      baseRevisionRef.current = serverBlock.revision;
      if (!lease) setLocalValue(serverBlock.content?.[valueKey] ?? null);
    }
  }, [serverBlock.revision, serverBlock.content, dirty, lease, valueKey]);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatTimer.current) {
      clearInterval(heartbeatTimer.current);
      heartbeatTimer.current = null;
    }
  }, []);

  const releasingRef = useRef(false);

  const doRelease = useCallback(async () => {
    if (releasingRef.current || !leaseRef.current) return;
    releasingRef.current = true;
    stopHeartbeat();
    const current = leaseRef.current;
    leaseRef.current = null; // guard against a second concurrent call (blur + button click) before this awaits
    setLease(null);
    try {
      await releaseBlockLease(workspaceId, docId, blockId, current.lease_id, current.lease_token);
    } catch {
      // best-effort — an unreachable release simply lets the lease expire
      // naturally via the live expires_at check, never leaving it stuck.
    } finally {
      releasingRef.current = false;
    }
  }, [workspaceId, docId, blockId, stopHeartbeat]);

  useEffect(() => () => {
    // release on unmount without awaiting (component is gone regardless)
    if (leaseRef.current) {
      releaseBlockLease(workspaceId, docId, blockId, leaseRef.current.lease_id, leaseRef.current.lease_token).catch(() => {});
    }
    stopHeartbeat();
    if (saveTimer.current) clearTimeout(saveTimer.current);
  }, [workspaceId, docId, blockId, stopHeartbeat]);

  const startEditing = useCallback(async () => {
    if (isViewAsClient || lease) return;
    setSaveState("idle");
    try {
      const acquired = await acquireBlockLease(workspaceId, docId, blockId);
      setLease(acquired);
      heartbeatTimer.current = setInterval(() => {
        heartbeatBlockLease(workspaceId, docId, blockId, acquired.lease_id, acquired.lease_token).catch(() => {
          // a failed renew is surfaced on the NEXT save attempt (403), not here
        });
      }, HEARTBEAT_INTERVAL_MS);
    } catch (err) {
      if (err.status === 409) setSaveState("locked");
      else setSaveState("error");
    }
  }, [isViewAsClient, lease, workspaceId, docId, blockId]);

  const flushSave = useCallback(async (value) => {
    const current = leaseRef.current;
    if (!current) return;
    setSaveState("saving");
    try {
      const result = await saveBlockContent(workspaceId, docId, blockId, {
        content: { ...serverBlock.content, [valueKey]: value },
        expectedBlockRevision: baseRevisionRef.current,
        leaseId: current.lease_id,
        leaseToken: current.lease_token,
        idempotencyKey: idempotencyKeyRef.current,
      });
      baseRevisionRef.current = result.revision;
      setDirty(false);
      setSaveState("saved");
      setConflictDoc(null);
      clearDraft(docId, blockId);
    } catch (err) {
      if (err.status === 409) {
        setSaveState("conflict");
        setConflictDoc(err.body?.detail?.current || null);
        // the failed local edit is never discarded automatically — it stays
        // in localStorage until the user explicitly chooses keep/discard.
        writeDraft(docId, blockId, value, baseRevisionRef.current);
      } else {
        setSaveState("error");
      }
    }
  }, [workspaceId, docId, blockId, serverBlock.content, valueKey]);

  // Registered once per block; always reads the LATEST dirty/localValue/
  // flushSave via this ref (kept fresh every render) rather than closing
  // over a stale snapshot from whenever the effect last ran.
  const latestRef = useRef();
  latestRef.current = { dirty, localValue, flushSave };
  useEffect(() => {
    return registerBlockFlush(docId, async () => {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
      if (latestRef.current.dirty) await latestRef.current.flushSave(latestRef.current.localValue);
    });
  }, [docId, blockId]);

  const onChangeValue = useCallback((value) => {
    setLocalValue(value);
    setDirty(true);
    writeDraft(docId, blockId, value, baseRevisionRef.current);
    if (!idempotencyKeyRef.current || saveState !== "saving") {
      idempotencyKeyRef.current = `${blockId}:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
    }
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => flushSave(value), AUTOSAVE_DEBOUNCE_MS);
  }, [docId, blockId, saveState, flushSave]);

  const stoppingRef = useRef(false);

  const stopEditing = useCallback(async () => {
    if (stoppingRef.current || !leaseRef.current) return;
    stoppingRef.current = true;
    try {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        if (dirty) await flushSave(localValue);
      }
      await doRelease();
    } finally {
      stoppingRef.current = false;
    }
  }, [dirty, localValue, flushSave, doRelease]);

  const keepLocalDraft = useCallback(() => {
    if (!recoverableDraft) return;
    setLocalValue(recoverableDraft.value);
    setDirty(true);
    setRecoverableDraft(null);
  }, [recoverableDraft]);

  const discardLocalDraft = useCallback(() => {
    clearDraft(docId, blockId);
    setRecoverableDraft(null);
  }, [docId, blockId]);

  return {
    isMine: !!lease,
    isLockedByOther: serverBlock.state === "editing_by_other",
    localValue,
    localText: localValue, // backward-compatible alias for pre-existing text-only callers
    dirty,
    saveState,
    conflictDoc,
    recoverableDraft,
    startEditing,
    stopEditing,
    onChangeValue,
    onChangeText: onChangeValue, // backward-compatible alias
    keepLocalDraft,
    discardLocalDraft,
    acceptLatestOnConflict: () => {
      baseRevisionRef.current = conflictDoc?.revision ?? baseRevisionRef.current;
      setLocalValue(conflictDoc?.content?.[valueKey] ?? localValue);
      setDirty(false);
      setSaveState("idle");
      setConflictDoc(null);
      clearDraft(docId, blockId);
    },
  };
}
